// Wires up ↑/↓ to move a "focused index" through a list and Enter to
// activate it. Skips when the user is typing in an input (otherwise
// arrow keys would fight the search box).

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Options {
  count: number;
  hrefAt: (index: number) => string | null;
}

interface Result {
  focusedIndex: number;
  setFocusedIndex: (i: number) => void;
  itemRef: (index: number) => (node: HTMLElement | null) => void;
}

export function useListKeyboardNav({ count, hrefAt }: Options): Result {
  const navigate = useNavigate();
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

  // Reset focus when the list size changes (e.g., search filters down).
  useEffect(() => {
    if (focusedIndex >= count) setFocusedIndex(-1);
  }, [count, focusedIndex]);

  const itemRef = useCallback(
    (index: number) =>
      (node: HTMLElement | null) => {
        if (node) itemRefs.current.set(index, node);
        else itemRefs.current.delete(index);
      },
    [],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (count === 0) return;
      const target = e.target as HTMLElement | null;
      const isTyping = target && /input|textarea|select/i.test(target.tagName);
      // Allow Escape to clear focus from anywhere; arrows/Enter only when not typing.
      if (e.key === "Escape") {
        setFocusedIndex(-1);
        return;
      }
      if (isTyping) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(count - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter" && focusedIndex >= 0) {
        const href = hrefAt(focusedIndex);
        if (href) {
          e.preventDefault();
          navigate(href);
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [count, focusedIndex, hrefAt, navigate]);

  // Scroll the focused row into view when it changes.
  useEffect(() => {
    if (focusedIndex < 0) return;
    const node = itemRefs.current.get(focusedIndex);
    node?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusedIndex]);

  return { focusedIndex, setFocusedIndex, itemRef };
}
